import { CloseOutlined, MoreOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { Avatar, Button, Dropdown, Flex, Input, MenuProps, Typography, message } from "antd";
import dayjs from "dayjs";
import React from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { useRootPath } from "../../hooks/app.hook";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import useSpecialityReference from "../../hooks/useSpecialityReference";
import { deleteClient, getAllClients } from "../../service/client.service";
import {
  setCreateClientState,
  setCustomiseSectionState,
  setLoadingNotesState,
  setNotesViewVisible,
  setSelectedClient,
  setShowDemoSignupModal,
} from "../../slices/appStateSlice";
import { resetClientForm, setClientForm, setClients, setIsEditing } from "../../slices/clientSlice";
import { resetNotesDetails, setNotesDetails } from "../../slices/notesSlice";
import type { TClientForm } from "../../types/index.type";
import { sortClientsByName } from "../../utils/datamanipulation.utils";
import { getRandomAvatarColor } from "../../utils/ui.utils";
import EmptyStateClient from "../EmptyState/EmptyStateClient.component";
import styles from "./clientview.module.scss";

const ClientView = ({
  demoClients,
  addClientRef,
  clientViewRef,
}: {
  demoClients?: TClientForm[];
  addClientRef?: React.RefObject<HTMLDivElement>;
  clientViewRef?: React.RefObject<HTMLDivElement>;
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { referIndividualBy } = useSpecialityReference();
  const componentName = "ClientView";

  const [searchTerm, setSearchTerm] = React.useState("");
  const [filteredData, setFilteredData] = React.useState<TClientForm[]>(demoClients ? demoClients : []);
  const [debounceTimeout, setDebounceTimeout] = React.useState<NodeJS.Timeout>();

  const { rootPath } = useRootPath();

  const { selectedClient, isDemoPath, isMobilePath } = useAppSelector((state) => state.appState);

  const { isRecording, isPaused } = useAppSelector((state) => state.recordings);

  const { clients } = useAppSelector((state) => state.client);

  const selectClient = (client: TClientForm | null, showLoader = true) => {
    if (selectedClient?.id && client && client.id && selectedClient.id === client.id) {
      return;
    }
    if (showLoader) {
      dispatch(setLoadingNotesState(true));
    }
    dispatch(setSelectedClient(client));
    dispatch(setNotesViewVisible(false));
    dispatch(setNotesDetails(null));
    dispatch(setCustomiseSectionState(false));
    dispatch(resetNotesDetails());
  };

  const handleClientSelectWithNavigate = (client: TClientForm) => {
    selectClient(client);
    navigate(`${rootPath}/clients/${client.uuid}`);
  };

  const clearSelection = (e: React.MouseEvent<HTMLSpanElement>) => {
    e.stopPropagation();
    navigate(rootPath);
    selectClient(null);
  };

  const showClientView = () => {
    dispatch(setCreateClientState(true));
    dispatch(setIsEditing(false));
    dispatch(resetClientForm());
    dispatch(setNotesViewVisible(false));
    dispatch(setNotesDetails(null));
    dispatch(setCustomiseSectionState(false));
    dispatch(resetNotesDetails());
  };

  // biome-ignore lint: no need to add selectedClient to the dependency array
  React.useEffect(() => {
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }

    if (searchTerm === "" && !demoClients) {
      setFilteredData(clients);
      return;
    }

    // Set a new timeout to debounce the filtering function
    const timeout = setTimeout(() => {
      const filtered = clients.filter((item) => {
        const lowerSearchTerm = searchTerm.toLowerCase();
        const formattedDob = item.date_of_birth ? dayjs(item.date_of_birth).format("MM/DD/YYYY") : "";

        return (
          item.name.toLowerCase().includes(lowerSearchTerm) ||
          (item.mrn && item.mrn.toLowerCase().includes(lowerSearchTerm)) ||
          (formattedDob && formattedDob.includes(lowerSearchTerm))
        );
      });
      setFilteredData(filtered);
    }, 300); // Adjust the delay (in milliseconds) as needed

    // Save the timeout id to be able to clear it later
    setDebounceTimeout(timeout);
  }, [clients, searchTerm]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const fetchClients = () => {
    getAllClients()
      .then((response) => {
        const sortedClients = sortClientsByName(response.data);
        dispatch(setClients(sortedClients));
        setFilteredData(sortedClients);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  React.useEffect(() => {
    if (!demoClients) {
      fetchClients();
    }
  }, []);

  const handleMenu = (e: any) => {
    e.domEvent.stopPropagation();
    if (e.key === "1") {
      dispatch(setCreateClientState(true));
      if (selectedClient) dispatch(setClientForm(selectedClient));
      dispatch(setIsEditing(true));
    }
    if (e.key === "2") {
      if (isDemoPath) {
        dispatch(setShowDemoSignupModal(true));
        return;
      }
      Swal.fire({
        title: `Would you like to delete this ${referIndividualBy.toLowerCase()}?`,
        text: `The ${referIndividualBy.toLowerCase()}'s notes will still be available unless you delete them.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it!",
        cancelButtonText: "No, keep it",
      }).then((result) => {
        if (result.isConfirmed && selectedClient && selectedClient.id) {
          deleteClient(selectedClient.id)
            .then(() => {
              navigate(rootPath);
              fetchClients();
              dispatch(setSelectedClient(null));
              message.success(`${referIndividualBy} deleted successfully`);
            })
            .catch((error) => {
              console.error(error);
              message.error(`Failed to delete ${referIndividualBy.toLowerCase()}. Try Again later`);
            });
        }
      });
    }
  };

  const items: MenuProps["items"] = !isMobilePath
    ? [
        {
          key: "1",
          label: <span>Edit {referIndividualBy}</span>,
        },
        {
          key: "2",
          label: <span>Delete {referIndividualBy}</span>,
        },
      ]
    : [
        {
          key: "2",
          label: <span>Delete {referIndividualBy}</span>,
        },
      ];

  return (
    <Flex
      vertical
      gap={10}
      className={
        !isRecording && !isPaused
          ? styles[`${componentName}__container`]
          : isRecording
            ? styles[`${componentName}__container-active-recording`]
            : isPaused
              ? styles[`${componentName}__container-paused-recording`]
              : ""
      }
      ref={clientViewRef}
    >
      <Flex justify="space-between" align="center">
        <Typography className={styles[`${componentName}__container-title`]}>{referIndividualBy}s</Typography>
        <Button
          className="secondary-button"
          type="primary"
          size="middle"
          icon={<PlusOutlined />}
          onClick={showClientView}
          style={{ padding: "0 10px" }}
        >
          Add {referIndividualBy}
        </Button>
      </Flex>
      <Flex>
        <Input
          value={searchTerm}
          disabled={clients.length === 0}
          prefix={<SearchOutlined />}
          placeholder={`Search ${referIndividualBy}s`}
          onChange={handleInputChange}
          allowClear
          style={{ padding: "5px 10px" }}
        />
      </Flex>
      {
        <Flex
          vertical
          className={
            styles[`${componentName}__container-scroll${filteredData && filteredData.length === 0 ? "-empty" : ""}`]
          }
        >
          {filteredData && filteredData.length ? (
            filteredData.map((data, index) => {
              // Calculate inline details for MRN and DOB
              const formattedDob = data.date_of_birth ? dayjs(data.date_of_birth).format("MM/DD/YYYY") : "";
              const derivedAgeFromDob =
                data.date_of_birth && formattedDob ? dayjs().diff(dayjs(data.date_of_birth), "year") : null;
              const rawAge =
                typeof data.age === "number" ? data.age : data.age ? Number.parseInt(data.age.toString(), 10) : null;
              const displayAge = formattedDob ? derivedAgeFromDob : rawAge;

              const inlineDetails: string[] = [];
              if (formattedDob) {
                inlineDetails.push(formattedDob);
              } else if (displayAge !== null && !Number.isNaN(displayAge) && displayAge >= 0) {
                inlineDetails.push(`Age ${displayAge}`);
              }
              if (data.mrn) {
                inlineDetails.push(`MRN: ${data.mrn}`);
              }

              return (
                <Flex
                  key={index}
                  gap={10}
                  align="center"
                  justify="space-between"
                  className={`${styles[`${componentName}__container-client`]} ${
                    selectedClient && selectedClient.id === data.id
                      ? styles[`${componentName}__container-client-selected`]
                      : ""
                  }`}
                  onClick={() => handleClientSelectWithNavigate(data)}
                >
                  <Flex align="center" gap={10}>
                    <Avatar
                      size={"small"}
                      className="special-avatar"
                      style={{
                        backgroundColor: getRandomAvatarColor(data.name),
                        minWidth: "24px",
                      }}
                    >
                      {data.name.charAt(0).toUpperCase()}
                    </Avatar>
                    <Flex vertical>
                      <Typography className={`${styles[`${componentName}__container-client-name`]}`}>
                        {data.name}
                        {inlineDetails.length > 0 && (
                          <span style={{ fontSize: "11px", color: "#999", marginLeft: "8px", fontWeight: "normal" }}>
                            {inlineDetails.join(" · ")}
                          </span>
                        )}
                      </Typography>
                    </Flex>
                  </Flex>
                  {selectedClient && selectedClient.id === data.id && (
                    <Flex gap={10}>
                      <CloseOutlined style={{ color: "var(--indigo-700)" }} onClick={clearSelection} />
                      <Dropdown menu={{ items, onClick: handleMenu }} placement="bottomLeft" arrow trigger={["click"]}>
                        <MoreOutlined style={{ color: "var(--indigo-700)" }} />
                      </Dropdown>
                    </Flex>
                  )}
                </Flex>
              );
            })
          ) : (
            <EmptyStateClient />
          )}
        </Flex>
      }
    </Flex>
  );
};

export default ClientView;
